import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import consumer from "../channels/consumer"
import { canvasResize, PHOTO_THUMBNAIL, BLUR_PLACEHOLDER } from "../lib/image_resize"
import { csrfToken, postJson } from "../lib/http"
import { uploadThumbnailsInChunks, directUploadImage } from "../lib/thumbnail_backfill"

const BACKFILL_COOLDOWN_MS = 60_000
const DISPLAY_FETCH_TIMEOUT_MS = 30_000
const DISPLAY_RETRY_MS = 3_000
const DISPLAY_MAX_ATTEMPTS = 3
const lastBackfillAt = new Map()

function currentUserKey() {
  return document.querySelector("[data-notification-badge-user-id-value]")
    ?.dataset.notificationBadgeUserIdValue || "anonymous"
}

// Bridge controller on the discoveries feed + match page (native only).
//
// 1. Display: replaces [data-client-ref] placeholders with the actual photo from
//    THIS device's library (the only place that photo can come from live).
// 2. Backfill: uploads thumbnails for this user's MATCHED photos to the DB so the
//    OTHER person can see them — proactively when matching completes (MatchingChannel
//    push) and as a catch-up on connect. Matched photos only, never the whole library.
//
// Does nothing on web (BridgeComponent.shouldLoad returns false without a bridge).
export default class extends BridgeComponent {
  static component = "photo-library"

  _photoAccountID() {
    const value = document.querySelector('meta[name="photo-updates-account"]')?.content
    return value ? Number(value) : null
  }

  connect() {
    super.connect()
    this._accountAtConnect = currentUserKey()
    this._connected = true
    this._displayPending = new Set()
    this._displayAttempts = new Map()
    this._displayRetryTimers = new Map()
    this._fetchErrors = new Map()
    setTimeout(() => this._loadAll(), 150)
    this._onPhotoWake = (event) => {
      if (document.visibilityState === "hidden" || event?.detail?.visible === false) return
      this._displayAttempts.clear()
      this._loadAll()
      this._backfillMatched({ force: true })
    }
    document.addEventListener("visibilitychange", this._onPhotoWake)
    document.addEventListener("native:restore", this._onPhotoWake)
    document.addEventListener("native:tab-visibility", this._onPhotoWake)

    // Infinite scroll appends more cards AFTER connect() (via Turbo stream). Stimulus
    // doesn't re-run connect() for appended children and there's no other hook, so the
    // initial _loadAll() would never see those later pages — their device-sourced
    // photos stay blank. Watch for newly-added [data-client-ref] placeholders and run
    // the same fill-in pass over them. Re-running _loadAll is safe: a filled
    // placeholder becomes an <img> with no data-client-ref, so it's never re-found,
    // and _fetchPhoto caches per identifier (no duplicate native fetches). The <img>
    // elements we insert have no data-client-ref, so they don't re-trigger us.
    this._appendObserver = new MutationObserver((mutations) => {
      const hasNewPlaceholders = mutations.some((m) =>
        Array.from(m.addedNodes).some((n) =>
          n.nodeType === Node.ELEMENT_NODE &&
          (n.matches?.("[data-client-ref]") || n.querySelector?.("[data-client-ref]"))
        )
      )
      if (!hasNewPlaceholders) return
      // Coalesce the burst of mutations from one appended page into a single pass.
      clearTimeout(this._appendTimer)
      this._appendTimer = setTimeout(() => this._loadAll(), 50)
    })
    this._appendObserver.observe(this.element, { childList: true, subtree: true })

    // When someone reveals (discovery-card#approve), upload THAT photo's clear image
    // right now — the background prep may not have finished it, and a group reveal has
    // no other way to get it onto S3 (others would see "developing…"). See _uploadNow.
    this._onUploadNow = (e) => this._uploadNow(e.detail?.clientRefs || [])
    document.addEventListener("photo-fetch:upload-now", this._onUploadNow)

    // Catch up whenever Action Cable connects, including after a dropped
    // connection. The feed refresh closes the gap for broadcasts missed while
    // offline; thumbnail backfill does the same for newly matched device photos.
    this._subscription = consumer.subscriptions.create(
      { channel: "MatchingChannel" },
      {
        connected: () => {
          this._backfillMatched()
          document.dispatchEvent(new CustomEvent("matching:complete"))
        },
        received: (data) => {
          if (data?.type === "timeline_updated") {
            document.dispatchEvent(new CustomEvent("feed:updated"))
            return
          }
          if (data?.type !== "matching_complete") return
          this._backfillMatched({ force: true })
          document.dispatchEvent(new CustomEvent("matching:complete"))
        }
      }
    )
  }

  disconnect() {
    this._connected = false
    this._appendObserver?.disconnect()
    clearTimeout(this._appendTimer)
    for (const timer of this._displayRetryTimers.values()) clearTimeout(timer)
    this._displayRetryTimers.clear()
    document.removeEventListener("visibilitychange", this._onPhotoWake)
    document.removeEventListener("native:restore", this._onPhotoWake)
    document.removeEventListener("native:tab-visibility", this._onPhotoWake)
    document.removeEventListener("photo-fetch:upload-now", this._onUploadNow)
    this._subscription?.unsubscribe()
    super.disconnect()
  }

  // Upload the clear image for specific photos immediately, at high priority. Fired
  // on reveal so the just-revealed photo lands on S3 now instead of whenever the
  // best-effort background pass gets to it. No-op for already-uploaded photos (the
  // caller only sends refs whose cell is still a device placeholder).
  async _uploadNow(clientRefs) {
    for (const ref of clientRefs) {
      if (!this._canUpload()) return
      if (!ref || ref.startsWith("web-")) continue
      const src = await this._fetchPhoto(ref, { priority: "high", timeout: 20000 })
      if (src) this._uploadThumbnail(ref, src)
    }
  }

  // ── Display: swap placeholders with device photos ─────────────────────────

  _loadAll() {
    if (!this._canUpload()) return
    const groups = {}
    this.element.querySelectorAll("[data-client-ref]").forEach(el => {
      const ref = el.dataset.clientRef
      if (!ref) return
      if (!groups[ref]) groups[ref] = []
      groups[ref].push(el)
    })

    Object.entries(groups).forEach(([localIdentifier, elements]) => {
      this._fetchAndApply(localIdentifier, elements)
    })
  }

  async _fetchAndApply(localIdentifier, elements) {
    // Skip web-uploaded photos — they're not in the device photo library.
    if (localIdentifier.startsWith("web-")) return
    if (!this._canUpload() || this._displayPending.has(localIdentifier)) return
    const attempts = this._displayAttempts.get(localIdentifier) || 0
    if (attempts >= DISPLAY_MAX_ATTEMPTS) return
    this._displayPending.add(localIdentifier)
    this._displayAttempts.set(localIdentifier, attempts + 1)

    // High priority: this photo is on screen right now, so it jumps ahead of the
    // background prep in the fetch queue.
    let src
    try {
      src = await this._fetchPhoto(localIdentifier, { priority: "high", timeout: DISPLAY_FETCH_TIMEOUT_MS })
    } finally {
      this._displayPending.delete(localIdentifier)
    }
    if (!this._canUpload()) return
    if (!src) {
      if (this._fetchErrors.get(localIdentifier) === "not_found") {
        this._showUnavailable(elements)
      }
      if ((this._displayAttempts.get(localIdentifier) || 0) < DISPLAY_MAX_ATTEMPTS &&
          !this._displayRetryTimers.has(localIdentifier)) {
        const timer = setTimeout(() => {
          this._displayRetryTimers.delete(localIdentifier)
          this._loadAll()
        }, DISPLAY_RETRY_MS)
        this._displayRetryTimers.set(localIdentifier, timer)
      }
      return
    }
    this._displayAttempts.delete(localIdentifier)

    elements.forEach(el => {
      if (!el.isConnected) return
      const img = document.createElement("img")
      img.src             = src
      img.style.width     = "100%"
      img.style.height    = "100%"
      img.style.objectFit = "cover"
      img.style.display   = "block"

      // A device-backed photo starts as a [data-client-ref] placeholder. Preserve
      // its lightbox wiring when swapping in the real image so one visible photo
      // can open even while the other side of the match remains unrevealed.
      Array.from(el.attributes).forEach(({ name, value }) => {
        if (name === "data-action" || name.startsWith("data-lightbox-")) {
          img.setAttribute(name, value)
        }
      })
      if (el.dataset.lightboxLabel) img.alt = el.dataset.lightboxLabel
      if (el.dataset.action?.includes("photo-lightbox#open")) img.style.cursor = "zoom-in"
      el.replaceWith(img)
    })

    this._uploadThumbnail(localIdentifier, src)
  }

  _showUnavailable(elements) {
    elements.forEach(el => {
      if (!el.isConnected || el.querySelector("[data-photo-unavailable]")) return
      const content = document.createElement("div")
      content.dataset.photoUnavailable = ""
      content.style.textAlign = "center"
      const icon = document.createElement("div")
      icon.textContent = "📷"
      icon.setAttribute("aria-hidden", "true")
      const label = document.createElement("div")
      label.textContent = "Photo unavailable"
      label.style.fontSize = "12px"
      label.style.marginTop = "8px"
      label.style.lineHeight = "1.4"
      content.append(icon, label)
      el.replaceChildren(content)
    })
  }

  // Fetch one photo from this device, as a data URL (or null). Serialized and
  // deduped: the Hotwire bridge keys replies by event name, so two concurrent
  // "fetchPhoto" sends clobber each other's callback (the display fetch and the
  // thumbnail-backfill fetch were racing — one callback never fired, leaving a
  // permanent camera icon). One send at a time, one fetch per identifier.
  // Fetches run one at a time (the native bridge can't handle concurrent fetchPhoto
  // calls), but through two priority lanes: "high" for photos the user is looking at
  // right now (the display swap), "low" for the background prep. A high request jumps
  // ahead of all not-yet-started low work, so on-screen photos don't wait behind the
  // backfill. If a photo is already queued low and then scrolls into view, it's
  // promoted instead of fetched twice (same cache key).
  _fetchPhoto(localIdentifier, { fast = false, timeout = 8000, priority = "low" } = {}) {
    this._cache ||= {}
    this._tasks ||= {}
    // Key by mode: the fast (blur) and high-quality fetches return different images
    // for the same identifier, so they must not share a cache slot.
    const key = (fast ? "fast:" : "") + localIdentifier
    if (key in this._cache) {
      if (priority === "high") this._promote(key)
      return this._cache[key]
    }

    const task = { key, priority, started: false, run: null }
    const result = new Promise((resolve) => {
      task.run = () => new Promise((done) => {
        task.started = true
        if (!fast) this._fetchErrors.delete(localIdentifier)
        let finished = false
        const finish = (value) => { if (!finished) { finished = true; resolve(value); done() } }
        // Don't let a dropped reply stall the queue. The high-quality path may pull a
        // full image from iCloud, so its caller passes a longer timeout.
        setTimeout(() => finish(null), timeout)
        this.send("fetchPhoto", { localIdentifier, fast, accountID: this._photoAccountID() }, (response) => {
          if (finished) return
          const error = response?.data?.error || response?.error
          if (!fast) {
            if (error) this._fetchErrors.set(localIdentifier, error)
            else this._fetchErrors.delete(localIdentifier)
          }
          const base64   = response?.data?.base64   || response?.base64
          const mimeType = response?.data?.mimeType || response?.mimeType || "image/jpeg"
          finish(base64 ? `data:${mimeType};base64,${base64.replace(/\s/g, "")}` : null)
        })
      })
      this._tasks[key] = task
      this._enqueue(task)
    })

    this._cache[key] = result
    // Don't remember failures: a timed-out / iCloud-stalled fetch should be retryable
    // on the next pass rather than cached as "no photo".
    result.then((v) => { delete this._tasks[key]; if (!v) delete this._cache[key] })
    return result
  }

  _enqueue(task) {
    this._highQueue ||= []
    this._lowQueue ||= []
    ;(task.priority === "high" ? this._highQueue : this._lowQueue).push(task)
    this._pump()
  }

  // Bump a still-queued background fetch into the high lane (a photo scrolled into view).
  _promote(key) {
    const task = this._tasks?.[key]
    if (!task || task.started || task.priority === "high") return
    task.priority = "high"
    const i = this._lowQueue?.indexOf(task) ?? -1
    if (i !== -1) { this._lowQueue.splice(i, 1); this._highQueue.push(task) }
  }

  async _pump() {
    if (this._pumping) return
    this._pumping = true
    try {
      while (this._highQueue?.length || this._lowQueue?.length) {
        const task = (this._highQueue.length ? this._highQueue : this._lowQueue).shift()
        await task.run()
      }
    } finally {
      this._pumping = false
    }
  }

  // ── Backfill: upload this user's matched-photo thumbnails to the DB ────────

  _canUpload() {
    return this._connected && currentUserKey() === this._accountAtConnect
  }

  async _backfillMatched({ force = false } = {}) {
    if (!this._canUpload()) return
    const now = Date.now()
    const userKey = currentUserKey()
    if (this._backfilling || (!force && now - (lastBackfillAt.get(userKey) || 0) < BACKFILL_COOLDOWN_MS)) return

    lastBackfillAt.set(userKey, now)
    this._backfilling = true
    try {
      const res = await fetch("/library_photos/match_missing_thumbnails", {
        headers: { "X-CSRF-Token": csrfToken() }
      })
      if (!res.ok) return
      const { client_refs } = await res.json()
      await uploadThumbnailsInChunks(client_refs, (ref) => this._fetchThumbnail(ref), {
        // Pass 1 (blur): iOS fast image path — quick + reliable even for iCloud
        // photos, so every match gets a blurred preview without stalling on the
        // slow high-quality pass.
        shouldContinue: () => this._canUpload(),
        fetchBlurSource: (ref) => this._fetchPhoto(ref, { fast: true, timeout: 8000 })
      })
    } catch (e) {
      // Non-fatal — thumbnails are cosmetic.
    } finally {
      this._backfilling = false
    }
  }

  // Fetch a photo from this device and return a resized thumbnail data URL, or null.
  async _fetchThumbnail(localIdentifier) {
    // High-quality clear image (shown after reveal) — allow longer for iCloud pulls.
    const src = await this._fetchPhoto(localIdentifier, { timeout: 20000 })
    if (!src) return null
    return canvasResize(src, PHOTO_THUMBNAIL)
  }

  async _uploadThumbnail(clientRef, dataUrl) {
    if (!this._canUpload()) return
    // Save the blur on its own first, so the other person gets a blurred preview
    // (not the off-white "Not revealed yet" lock) even if the S3 image upload below
    // fails. The blur is a tiny base64 column — no S3 involved.
    const blur = await canvasResize(dataUrl, BLUR_PLACEHOLDER)
    if (blur && this._canUpload()) {
      postJson("/library_photos/thumbnails", {
        thumbnails: [{ client_ref: clientRef, blur }]
      }).catch(() => {})
    }

    const thumbnail = await canvasResize(dataUrl, PHOTO_THUMBNAIL)
    if (!thumbnail || !this._canUpload()) return
    try {
      // Image goes straight to S3 (direct upload); the server only gets the blob id.
      const signedId = await directUploadImage(thumbnail, clientRef, { shouldContinue: () => this._canUpload() })
      if (!this._canUpload()) return
      postJson("/library_photos/thumbnails", {
        thumbnails: [{ client_ref: clientRef, signed_id: signedId, blur }]
      }).catch(() => {})
    } catch (e) {
      console.warn("[photo-fetch] direct upload failed for", clientRef, e)
    }
  }
}
